/**
 * „Alles da“ -- erst prüfen, dann buchen (ADR 0013 §8, ADR 0015).
 *
 * Alles, was im Teilbaum seinen Stammplatz hat und nicht dort ist, steht hier
 * als Bewegung, gruppiert nach Ziel: was, von wo, wohin. Jede Zeile ist
 * abwählbar. Wird eine Kiste abgewählt, gehen auch die Zeilen mit, die in sie
 * hineinführen -- einzeln lassen sie sich wieder anwählen.
 *
 * Eine **bewusste Ausnahme** von CLAUDE.md §7: Die Übersicht fragt, bevor sie
 * bucht. Der Nutzer hat es so entschieden -- sonst denkt einer, alles sei da,
 * ein Teil fehlt und wird unbemerkt eingebucht. Ausgelöst wird sie
 * absichtlich, und „Abbrechen“ geht immer.
 *
 * Was einen Status mit „Stammplatz-Rückbuchung ignorieren“ hat (defekt, in
 * Prüfung), bleibt liegen und steht einzeln unter „Bleibt liegen“ (ADR 0025).
 *
 * Alles aus der lokalen Kopie, also auch offline.
 */

import { useMemo, useState } from "react";
import {
  allesDaAuswahl,
  allesDaUmschalten,
  type AllesDaPlan,
  type AllesDaZeile,
} from "@thw/core-domain";
import { bestandLaden, useBestand, type Bestand } from "../lokal/bestand.js";
import { allesDaBuchen, allesDaFuer } from "../lokal/stammplatz.js";
import { woWar, type Gebucht } from "../lokal/verschieben.js";
import { Farbpunkt, Knopf, Ladeanzeige, Nummer, Rueckmeldung } from "./bausteine.jsx";

export interface AllesDaEigenschaften {
  lagerort_id: string;
  aufAbbruch: () => void;
  aufGebucht: (gebucht: Gebucht[], verboten: string[]) => void;
}

/** „Gitterbox GB-01“ oder „Leitungsroller 50 m · 0141-009003“. */
function artikelName(
  bestand: Bestand,
  artikel_id: string,
): { name: string; nummer: string | null } {
  const artikel = bestand.artikel.get(artikel_id);
  if (artikel === undefined) return { name: "unbekannter Artikel", nummer: null };
  // Ein Packstück heißt nach seiner Kennung, die Nummer braucht es nicht.
  for (const l of bestand.lagerorte.values()) {
    if (l.artikel_id === artikel_id) return { name: artikel.bezeichnung, nummer: null };
  }
  return { name: artikel.bezeichnung, nummer: artikel.inventarnummer };
}

/** Was bleibt, in einem Satz: „1 verliehen, 1 archiviert, Bohrhammer (fremd)“. */
function bleibtText(bestand: Bestand, plan: AllesDaPlan): string | null {
  const teile: string[] = [];
  if (plan.verliehen.length > 0) teile.push(`${String(plan.verliehen.length)} verliehen`);
  if (plan.archiviert.length > 0) {
    teile.push(`${String(plan.archiviert.length)} archiviert (verloren, gestohlen …)`);
  }
  if (plan.fremd.length > 0 && plan.fremd.length <= 3) {
    for (const id of plan.fremd) teile.push(`${artikelName(bestand, id).name} (fremd)`);
  } else if (plan.fremd.length > 3) {
    teile.push(`${String(plan.fremd.length)} fremd`);
  }
  if (plan.ohneStammplatz.length > 0) {
    teile.push(`${String(plan.ohneStammplatz.length)} ohne Stammplatz`);
  }
  return teile.length === 0 ? null : teile.join(", ");
}

export function AllesDa({ lagerort_id, aufAbbruch, aufGebucht }: AllesDaEigenschaften) {
  const bestand = useBestand();
  const [wahl, setWahl] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const plan = useMemo(
    () => (bestand === undefined ? undefined : allesDaFuer(bestand, lagerort_id)),
    [bestand, lagerort_id],
  );
  const gewaehlt = useMemo(
    () => (plan === undefined ? new Set<string>() : allesDaAuswahl(plan, wahl)),
    [plan, wahl],
  );

  /** Die Zeilen nach Ziel, in Buchungsreihenfolge: erst die Kisten, dann ihr Inhalt. */
  const gruppen = useMemo(() => {
    const nachZiel = new Map<string, AllesDaZeile[]>();
    for (const z of plan?.zeilen ?? []) {
      const liste = nachZiel.get(z.stammplatz_id);
      if (liste === undefined) nachZiel.set(z.stammplatz_id, [z]);
      else liste.push(z);
    }
    return [...nachZiel];
  }, [plan]);

  const buchen = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      // Frisch gelesen: Seit dem Öffnen kann sich etwas bewegt haben.
      const { gebucht, verboten } = await allesDaBuchen(
        await bestandLaden(),
        lagerort_id,
        gewaehlt,
      );
      aufGebucht(gebucht, verboten);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
      setLaeuft(false);
    }
  };

  const ort = bestand?.lagerorte.get(lagerort_id);
  const titel = `Alles da – ${ort === undefined ? "" : ort.standardziel ? ort.bezeichnung : ort.kennung}`;
  const anzahl = gewaehlt.size;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Alles da"
      className="fixed inset-0 z-30 overflow-y-auto bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-blue-800 px-4 py-2 text-white">
        <h2 className="text-lg font-bold">{titel}</h2>
        <button
          type="button"
          onClick={aufAbbruch}
          className="min-h-12 rounded-lg px-3 font-semibold underline active:bg-blue-900"
        >
          Abbrechen
        </button>
      </header>

      {bestand === undefined || plan === undefined ? (
        <Ladeanzeige was="Die Übersicht" />
      ) : (
        <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-28">
          {fehler !== null && <Rueckmeldung art="warnung" kopf="Nicht gebucht" zusatz={fehler} />}

          {plan.zeilen.length === 0 ? (
            <Rueckmeldung
              art="still"
              kopf={
                plan.liegenLassen.length === 0
                  ? "Alles ist an seinem Stammplatz"
                  : "Nichts zurückzubuchen"
              }
              zusatz={
                plan.liegenLassen.length === 0
                  ? "Hier ist nichts zurückzubuchen."
                  : "Was fehlt, bleibt wegen seines Status liegen."
              }
            />
          ) : (
            <p className="text-neutral-700 dark:text-neutral-200">
              Diese Bewegungen werden gebucht. Was nicht da ist, abwählen.
            </p>
          )}

          {gruppen.map(([ziel, zeilen]) => {
            const zielOrt = bestand.lagerorte.get(ziel);
            const zielName =
              zielOrt === undefined
                ? "unbekannten Lagerort"
                : zielOrt.standardziel
                  ? zielOrt.bezeichnung
                  : zielOrt.kennung;
            return (
              <section
                key={ziel}
                aria-label={`zurück in ${zielName}`}
                className="rounded-xl bg-white p-3 dark:bg-neutral-800"
              >
                <h3 className="mb-1 text-sm font-semibold text-neutral-500">
                  zurück in {zielName}
                </h3>
                <ul className="flex flex-col">
                  {zeilen.map((z) => {
                    const { name, nummer } = artikelName(bestand, z.artikel_id);
                    const jetzt = bestand.zustaende.get(z.artikel_id);
                    const von = jetzt === undefined ? "noch nicht erfasst" : woWar(bestand, jetzt);
                    const an = gewaehlt.has(z.artikel_id);
                    return (
                      <li key={z.artikel_id}>
                        <label className="flex min-h-14 cursor-pointer items-center gap-3 border-b border-neutral-200 py-2 last:border-b-0 dark:border-neutral-700">
                          <input
                            type="checkbox"
                            className="size-7 shrink-0 accent-blue-700"
                            checked={an}
                            aria-label={nummer === null ? name : `${name} · ${nummer}`}
                            onChange={() => {
                              setWahl(allesDaUmschalten(plan, wahl, z.artikel_id));
                            }}
                          />
                          <span className={`flex min-w-0 flex-col ${an ? "" : "opacity-60"}`}>
                            <span className="font-semibold">{name}</span>
                            <span className="flex flex-wrap gap-x-3 text-sm text-neutral-600 dark:text-neutral-300">
                              {nummer !== null && <Nummer>{nummer}</Nummer>}
                              <span>von: {von}</span>
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}

          {plan.liegenLassen.length > 0 && (
            <section
              aria-label="Bleibt liegen"
              className="rounded-xl border border-dashed border-neutral-400 p-3 dark:border-neutral-600"
            >
              <h3 className="mb-1 font-semibold">Bleibt liegen – wegen des Status</h3>
              <ul className="flex flex-col">
                {plan.liegenLassen.map((id) => {
                  const { name, nummer } = artikelName(bestand, id);
                  const jetzt = bestand.zustaende.get(id);
                  const art = bestand.statusArten.find((a) => a.schluessel === jetzt?.status);
                  const stammplatz = bestand.lagerorte.get(
                    bestand.artikel.get(id)?.stammplatz_id ?? "",
                  );
                  return (
                    <li
                      key={id}
                      className="flex min-h-12 flex-col justify-center border-b border-neutral-200 py-2 last:border-b-0 dark:border-neutral-700"
                    >
                      <span className="flex items-center gap-2 font-semibold">
                        {art !== undefined && <Farbpunkt farbe={art.farbe} />}
                        {name}
                      </span>
                      <span className="flex flex-wrap gap-x-3 text-sm text-neutral-600 dark:text-neutral-300">
                        {nummer !== null && <Nummer>{nummer}</Nummer>}
                        <span>{art?.bezeichnung ?? jetzt?.status}</span>
                        <span>
                          liegt {jetzt === undefined ? "noch nicht erfasst" : woWar(bestand, jetzt)}
                        </span>
                        {stammplatz !== undefined && (
                          <span>
                            gehört in{" "}
                            {stammplatz.standardziel ? stammplatz.bezeichnung : stammplatz.kennung}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {bleibtText(bestand, plan) !== null && (
            <p
              data-testid="alles-da-bleibt"
              className="rounded-xl border border-dashed border-neutral-400 p-3 text-sm text-neutral-700 dark:border-neutral-600 dark:text-neutral-200"
            >
              <span className="font-semibold">Bleibt unverändert:</span> {bleibtText(bestand, plan)}
            </p>
          )}
        </div>
      )}

      {plan !== undefined && plan.zeilen.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-10 flex justify-center border-t border-neutral-300 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-900">
          <div className="flex w-full max-w-2xl flex-col">
            <Knopf art="haupt" disabled={anzahl === 0 || laeuft} onClick={() => void buchen()}>
              {anzahl === 0
                ? "Nichts gewählt"
                : `${String(anzahl)} ${anzahl === 1 ? "Bewegung" : "Bewegungen"} buchen`}
            </Knopf>
          </div>
        </div>
      )}
    </div>
  );
}
